import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { motion, useReducedMotion } from 'motion/react'
import { Check, PanelLeft, Pin } from 'lucide-react'
import { toast } from 'sonner'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import { usePreferences, useUpdatePreferences, type Preferences } from './usePreferences'

/**
 * Sidebar settings: pinned or not (this browser), and what the collapsed rail does on hover —
 * slide open over the page, or magnify like the macOS Dock (saved to the account).
 */

export type SidebarBehavior = 'peek' | 'dock'

export function useSidebarBehavior(): SidebarBehavior {
  const prefs = usePreferences()
  return prefs.data?.sidebar?.behavior ?? 'peek'
}

const MAG_MIN = 44
const MAG_MAX = 112
const MAG_DEFAULT = 72

/**
 * How big Dock icons grow. Moves live while dragging (shared through the preferences cache so
 * the real Dock beside the dialog swells as you slide) and saves once you let go.
 */
function MagnificationSlider() {
  const prefs = usePreferences()
  const update = useUpdatePreferences()
  const saved = prefs.data?.sidebar?.magnification ?? MAG_DEFAULT
  const [value, setValue] = useState(saved)
  const timer = useRef<number | undefined>(undefined)
  useEffect(() => () => window.clearTimeout(timer.current), [])
  const pct = ((value - MAG_MIN) / (MAG_MAX - MAG_MIN)) * 100
  const queryClient = useQueryClient()
  const commit = (v: number) => {
    // Live: the Dock reads the cached preferences, so it swells at the new size right away.
    queryClient.setQueryData<Preferences>(['me-preferences'], (old) => (old ? { ...old, sidebar: { ...(old.sidebar ?? {}), magnification: v } } : old))
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => {
      update.mutate({ sidebar: { ...(prefs.data?.sidebar ?? {}), behavior: 'dock', magnification: v } }, { onError: () => toast.error('Could not save. Try again.') })
    }, 250)
  }
  return (
    <div className="rounded-xl border border-border p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-medium text-foreground">Magnification</span>
        <span className="num text-xs text-text-muted">{Math.round((value / 38) * 100)}%</span>
      </div>
      <div className="flex items-center gap-3">
        <span className="grid size-5 place-items-center rounded-md bg-surface-sunken"><span className="size-2 rounded-[3px] bg-text-muted" /></span>
        <input
          type="range"
          min={MAG_MIN}
          max={MAG_MAX}
          step={2}
          value={value}
          aria-label="Dock magnification"
          onChange={(e) => {
            const v = Number(e.target.value)
            setValue(v)
            commit(v)
          }}
          className="tt-range h-1.5 flex-1 cursor-pointer appearance-none rounded-full"
          style={{ background: `linear-gradient(to right, var(--color-brand) ${pct}%, var(--color-border) ${pct}%)` }}
        />
        <span className="grid size-7 place-items-center rounded-lg bg-surface-sunken"><span className="size-4 rounded-[5px] bg-text-muted" /></span>
      </div>
      <p className="mt-2 text-tiny text-text-muted">Hover the Dock on the left to try it as you slide.</p>
    </div>
  )
}

export function SidebarSettingsDialog({ open, onOpenChange, pinned, onPinnedChange }: { open: boolean; onOpenChange: (open: boolean) => void; pinned: boolean; onPinnedChange: (pinned: boolean) => void }) {
  const behavior = useSidebarBehavior()
  const prefs = usePreferences()
  const update = useUpdatePreferences()
  const choose = (next: SidebarBehavior) => {
    if (next === behavior) return
    update.mutate({ sidebar: { ...(prefs.data?.sidebar ?? {}), behavior: next } }, { onError: () => toast.error('Could not save. Try again.') })
    if (pinned) onPinnedChange(false)
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><PanelLeft className="size-5 text-brand" /> Sidebar</DialogTitle>
          <DialogDescription>Keep it open, or fold it to icons and choose how it answers your pointer.</DialogDescription>
        </DialogHeader>

        <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-border p-3">
          <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-brand-soft text-brand"><Pin className="size-4" /></span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-medium text-foreground">Pin the sidebar open</span>
            <span className="block text-xs text-text-secondary">Off folds it to icons. ⌘\ / Ctrl+\ switches any time.</span>
          </span>
          <Switch checked={pinned} onCheckedChange={onPinnedChange} />
        </label>

        <p className="text-xs font-semibold uppercase tracking-wide text-text-secondary">When folded</p>
        <div className="grid gap-3 sm:grid-cols-2" role="radiogroup" aria-label="When folded">
          <Choice active={behavior === 'peek'} onClick={() => choose('peek')} title="Slide open" detail="Hover the rail and the full sidebar glides out over the page.">
            <PeekPreview />
          </Choice>
          <Choice active={behavior === 'dock'} onClick={() => choose('dock')} title="Dock" detail="Icons swell under your pointer, with names beside them, like the Mac Dock.">
            <DockPreview />
          </Choice>
        </div>
        {behavior === 'dock' ? <MagnificationSlider /> : null}
      </DialogContent>
    </Dialog>
  )
}

function Choice({ active, onClick, title, detail, children }: { active: boolean; onClick: () => void; title: string; detail: string; children: ReactNode }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onClick}
      className={cn(
        'relative flex flex-col gap-2 rounded-2xl border p-2.5 text-left transition-[border-color,box-shadow,background-color]',
        active ? 'border-brand bg-brand-soft shadow-[0_0_0_1px_var(--color-brand)]' : 'border-border hover:bg-surface-sunken',
      )}
    >
      <div className="h-28 overflow-hidden rounded-xl bg-surface-sunken">{children}</div>
      <span className="px-1 text-sm font-semibold text-foreground">{title}</span>
      <span className="px-1 pb-1 text-tiny text-text-secondary">{detail}</span>
      {active ? <Check className="absolute right-3.5 top-3.5 size-4 rounded-full bg-brand p-0.5 text-text-inverse" /> : null}
    </button>
  )
}

/** A looping miniature of each behaviour, so the choice is seen rather than read. */
function PeekPreview() {
  const reduce = useReducedMotion()
  return (
    <div className="relative size-full">
      <div className="absolute inset-y-0 left-0 w-5 bg-nav-bg" />
      <motion.div
        className="absolute inset-y-0 left-0 bg-nav-bg shadow-xl"
        animate={reduce ? { width: 64 } : { width: [20, 20, 70, 70, 20] }}
        transition={{ duration: 3.2, repeat: Infinity, times: [0, 0.2, 0.4, 0.75, 1], ease: [0.32, 0.72, 0, 1] }}
      >
        <div className="grid gap-1.5 p-1.5 pt-3">
          {[0, 1, 2, 3].map((i) => <span key={i} className="block h-1.5 rounded-full bg-white/40" />)}
        </div>
      </motion.div>
      <div className="ml-8 grid gap-1.5 p-3">
        {[0, 1, 2].map((i) => <span key={i} className="block h-2 rounded bg-border" />)}
      </div>
    </div>
  )
}

function DockPreview() {
  const reduce = useReducedMotion()
  const sizes = [[10, 10, 12, 10], [10, 13, 17, 13], [12, 17, 13, 10], [17, 13, 10, 10]]
  return (
    <div className="relative size-full">
      <div className="absolute inset-y-0 left-0 flex w-7 flex-col items-start gap-1 bg-nav-bg py-2 pl-1">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <motion.span
            key={i}
            className="block rounded-[28%] bg-white/45"
            animate={reduce ? { width: 10, height: 10 } : { width: sizes.map((s) => s[i % 4]!), height: sizes.map((s) => s[i % 4]!) }}
            transition={{ duration: 2.8, repeat: Infinity, repeatType: 'mirror', ease: 'easeInOut' }}
          />
        ))}
      </div>
      <motion.span
        className="absolute left-9 top-9 rounded-md bg-black/50 px-1.5 py-0.5 text-[9px] font-medium text-white backdrop-blur"
        animate={reduce ? { opacity: 1 } : { opacity: [0, 1, 1, 0], y: [4, 0, 10, 14] }}
        transition={{ duration: 2.8, repeat: Infinity, repeatType: 'mirror' }}
      >
        Work
      </motion.span>
      <div className="ml-10 grid gap-1.5 p-3">
        {[0, 1, 2].map((i) => <span key={i} className="block h-2 rounded bg-border" />)}
      </div>
    </div>
  )
}
